import { Loader2, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { applyDiscount, BUYABLE_UPGRADE_TIERS, pickUpgradeTier } from './upgradeTier';
import { IndexPath } from '@/Config/app/router/index.path';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { SubscriptionIntervalEnum } from '@/Domain/enum/SubscriptionInterval.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useGetSubscriptionPlans } from '@/Domain/hooks/Subscription/useGetSubscriptionPlans.hook';
import { useUpdateSubscription } from '@/Domain/hooks/Subscription/useUpdateSubscription.hook';

interface SiteScrapeUpgradeProps {
  selectedCount: number;
  usedCount: number;
  currentLimit: number | null;
  planLimit: number | null;
  // Paying subscribers change plans on the subscription page (ChangePlanDialog
  // with proration preview) — never via an unconfirmed in-place update from here.
  isPaidPlan: boolean;
  onBeforeCheckout: () => Promise<void> | void;
  onManagePlan: () => Promise<void> | void;
  discountApplied: boolean;
  discountPercentOff: number;
  promoCode?: string;
}

/**
 * Over-limit upsell: picks the single cheapest plan whose article capacity covers
 * the existing articles plus the current selection and presents just that one as
 * a compact, focused CTA.
 */
export const SiteScrapeUpgrade = ({
  selectedCount,
  usedCount,
  currentLimit,
  planLimit,
  isPaidPlan,
  onBeforeCheckout,
  onManagePlan,
  discountApplied,
  discountPercentOff,
  promoCode,
}: SiteScrapeUpgradeProps) => {
  const { t, i18n } = useTranslation(['knowledge_base', 'getting_started', 'common']);
  const { data: plans } = useGetSubscriptionPlans();
  const [pending, setPending] = useState(false);
  const { capture } = usePostHogAnalytics();
  const navigate = useNavigate();

  const tier = pickUpgradeTier(BUYABLE_UPGRADE_TIERS, usedCount + selectedCount, currentLimit);

  const stripePriceId = tier ? plans?.products[tier.productKey]?.price_monthly : undefined;
  const displayPrice = tier?.displayPrice ?? '';
  const discountedPrice =
    tier && discountApplied && !isPaidPlan ? applyDiscount(displayPrice, discountPercentOff) : null;
  const planName = tier ? t(`getting_started:pricing_step.tiers.${tier.key}.name`) : '';

  const updateSubscription = useUpdateSubscription({
    successCallback: () => setPending(false),
    errorCallback: () => {
      setPending(false);
      toast.error(t('getting_started:pricing_step.checkout_error_toast'));
    },
    fromTier: TierEnum.FREE,
    toTier: tier?.id ?? TierEnum.FREE,
    interval: SubscriptionIntervalEnum.Monthly,
    source: 'site_scrape_review',
    promotionCode: discountApplied && !isPaidPlan ? promoCode : undefined,
  });

  const handleUpgrade = async () => {
    if (!tier || pending) return;
    capture(PostHogEventEnum.SiteScrapeUpgradeClicked, {
      selected_count: selectedCount,
      plan_limit: planLimit,
      to_tier: tier.id,
      method: isPaidPlan ? 'manage_plan' : 'checkout',
    });
    if (isPaidPlan) {
      await onManagePlan();
      navigate(IndexPath.subscription);
      return;
    }
    if (!stripePriceId) return;
    setPending(true);
    await onBeforeCheckout();
    void updateSubscription.handleUpdate(stripePriceId);
  };

  // No buyable plan covers existing articles + this selection: don't pitch a
  // plan that can't fit — just point at the deselect alternative.
  if (!tier) {
    return (
      <div
        className="flex shrink-0 flex-col gap-1 rounded-xl border border-border bg-muted/60 p-3"
        data-testid="site-scrape-review-upgrade-none"
      >
        {planLimit !== null && (
          <p className="text-center text-xs text-muted-foreground">
            {t('tabs.website.review.smart_upgrade_deselect', { limit: planLimit })}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex shrink-0 flex-col gap-2.5 rounded-xl border border-info/20 bg-gradient-to-b from-info-muted/40 to-card p-3 shadow-sm"
      data-testid="site-scrape-review-upgrade"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-info-muted px-2 py-0.5 text-xs font-medium text-info">
          <Sparkles className="size-3" />
          {t('tabs.website.review.smart_upgrade_badge')}
        </span>
        <div className="flex items-baseline gap-1.5">
          {discountedPrice && (
            <span className="text-xs font-medium text-muted-foreground line-through">
              {displayPrice}
            </span>
          )}
          <span className="text-xl font-semibold tracking-tight text-foreground">
            {discountedPrice ?? displayPrice}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('getting_started:pricing_step.per_month')}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold tracking-tight text-foreground">
          {t('tabs.website.review.smart_upgrade_title', { count: selectedCount })}
        </p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t('tabs.website.review.smart_upgrade_subtitle', {
            plan: planName,
            articles: tier.articles.toLocaleString(i18n.language),
          })}
        </p>
      </div>

      <Button
        variant="dark"
        size="sm"
        onClick={() => void handleUpgrade()}
        disabled={pending || (!isPaidPlan && !stripePriceId)}
        className="w-full"
        data-testid="site-scrape-review-upgrade-cta"
      >
        {pending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : isPaidPlan ? (
          t('common:upgrade.action')
        ) : (
          t('tabs.website.review.smart_upgrade_cta', { plan: planName })
        )}
      </Button>

      {planLimit !== null && (
        <p className="text-center text-xs text-muted-foreground">
          {t('tabs.website.review.smart_upgrade_deselect', { limit: planLimit })}
        </p>
      )}
    </div>
  );
};
